import React, { useState, useEffect, useCallback, useRef } from 'react';
import CalendarView from './components/CalendarView/CalendarView';
import OptionsView from './components/OptionsView/OptionsView';
import UserView from './components/UserView/UserView';
import HelpView from './components/HelpView/HelpView';
import TagManager from 'react-gtm-module';
import { isAndroid, isIOS } from 'react-device-detect';
import { enqueue, flush, installFlushHooks } from './offlineQueue';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestion, faThumbsUp } from '@fortawesome/free-solid-svg-icons';

const isMobileUA = () =>
  (('ontouchstart' in window) || navigator.maxTouchPoints > 0) &&
  window.matchMedia('(pointer:coarse)').matches;

const useWindowMetrics = () => {
  const compute = () => {
    const mobile = isMobileUA();
    const portrait = window.innerHeight >= window.innerWidth;
    const horizontal = !portrait;

    if (!mobile) {
      if (horizontal) {
        return {
          windowWidth: window.innerWidth,
          windowHeight: window.innerHeight,
          devicePixelRatio: window.devicePixelRatio || 1,
          deviceWidth: window.innerWidth,
          deviceHeight: window.innerHeight,
          mobile,
          portrait,
          horizontal,
        };
      }

      // Desktop portrait/narrow windows: force virtual phone metrics.
      return {
        windowWidth: 480,
        windowHeight: 960,
        devicePixelRatio: 2,
        deviceWidth: 480,
        deviceHeight: 960,
        mobile,
        portrait: true,   // treat as portrait layout
        horizontal: false,
      };
    }

    // Mobile: use real values (but we’ll gate rendering on portrait)
    return {
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 2,
      deviceWidth: window.outerWidth || window.innerWidth,
      deviceHeight: window.outerHeight || window.innerHeight,
      mobile,
      portrait,
      horizontal,
    };
  };
  const [size, setSize] = useState(compute);

  useEffect(() => {
    const onResize = () => setSize(compute());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return size;
}

function Schedule() {
  const {
    windowWidth, windowHeight, devicePixelRatio,
    deviceWidth, deviceHeight, horizontal
  } = useWindowMetrics();

  const optionsBarHeight = 44;
  const bottomPadding = isAndroid ? 68 : 28;
  const tableSideMargins = isAndroid ? 35 : 0;
  const statusBarPadding = (deviceHeight === windowHeight && isIOS) ? 59 : 0;
  const topPadding = bottomPadding;

  const [scheduleSelection, setScheduleSelection] = useState(localStorage.getItem('scheduleSelection') ? parseInt(localStorage.getItem('scheduleSelection')) : 4);

  const [userGUID, setUserGUID] = useState(localStorage.getItem('userGUID') ? localStorage.getItem('userGUID') : process.env.REACT_APP_GOOGLE_IMPERSONATION_USER_GUID || null);
  const [selectedGroupGUID, setSelectedGroupGUID] = useState(localStorage.getItem('selectedGroupGUID') ? localStorage.getItem('selectedGroupGUID') : '');
  const [userSelections, setUserSelections] = useState(localStorage.getItem(`savedSelections-${userGUID}`) ? JSON.parse(localStorage.getItem(`savedSelections-${userGUID}`)) : { user: null, selections: [] });
  const [page, setPage] = useState(localStorage.getItem('page') || 'help');
  const [json, setJson] = useState(null);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalArtist, setInfoModalArtist] = useState('');
  const [friendSchedules, setFriendSchedules] = useState([]);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const useOnlineStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }, []);

    return isOnline;
  }
  const online = useOnlineStatus();

  useEffect(() => {
    installFlushHooks();
  }, []);

  const IDEM = () => crypto.randomUUID();

  const handleChangeUserSelections = (id, ranking, artist) => {
    if (ranking < 1) {
      const friendSchedulesArr = ([...json]
        .map(item => item.stages)
        .flat()
        .map(item => item.sets)
        .flat()
        .find(item => item.id === id)?.friendSchedules || []
      );
      if (online && friendSchedulesArr.length > 0) {
        setFriendSchedules(friendSchedulesArr);
        setShowInfoModal(true);
        setInfoModalArtist(artist);
      }
      return;
    };
    let newSelections = [...userSelections.selections];
    const selectionIndex = newSelections.findIndex(item => item.id === id);

    if (selectionIndex === -1) {
      newSelections.push({ id: id, rank: scheduleSelection, sync: false });
      logButtonClick(artist, scheduleSelection);
      upsertUserSchedule(id, scheduleSelection)
    } else if (newSelections[selectionIndex].rank === ranking) {
      newSelections.splice(selectionIndex, 1); // Remove the object at selectionIndex
      logButtonClick(artist, 0);
      deleteUserSchedule(id);
    } else {
      newSelections[selectionIndex] = { id: id, rank: scheduleSelection, sync: false };
      logButtonClick(artist, scheduleSelection);
      upsertUserSchedule(id, scheduleSelection)
    }
    setUserSelections({
      ...userSelections,
      selections: newSelections
    });
    localStorage.setItem(`savedSelections-${userGUID}`, JSON.stringify({
      ...userSelections,
      selections: newSelections
    }))
  }

  const upsertUserSchedule = async (setGUID, status) => {
    if (!userGUID) return;
    fetch(`/api/user-schedule/`, {
      method: 'PUT',
      body: JSON.stringify({
        userScheduleUserGUID: userGUID,
        userScheduleSetGUID: setGUID,
        userScheduleStatus: status
      }),
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': IDEM() },
    })
      .then(r => { if (!r.ok) throw new Error(String(r.status)); })
      .catch((error) => {
        // cache for later if offline
        enqueue({
          type: 'UPSERT_SCHEDULE',
          key: `${userGUID}:${setGUID}`,
          url: '/api/user-schedule/',
          method: 'PUT',
          body: {
            userScheduleUserGUID: userGUID,
            userScheduleSetGUID: setGUID,
            userScheduleStatus: status
          }
        });
        // optional: try flushing right away
        flush();
      })
  };

  const deleteUserSchedule = async (setGUID) => {
    if (!userGUID) return;
    const url = `/api/user-schedule/${userGUID}/${setGUID}`;
    fetch(url, {
      method: 'DELETE',
      headers: { 'Idempotency-Key': IDEM() }
    })
      .then(r => { if (!r.ok) throw new Error(String(r.status)); })
      .catch((error) => {
        // cache for later if offline
        enqueue({
          type: 'DELETE_SCHEDULE',
          key: `${userGUID}:${setGUID}`,
          url,
          method: 'DELETE'
        });
        flush();
      });
  };

  const logButtonClick = (buttonName, additionalInfo) => {
    const tagManagerArgs = {
      gtmId: 'GTM-NJCT8JRC',
      dataLayer: {
        userProject: 'edco24',
        events: {
          event: buttonName,
          info: additionalInfo
        }
      },
      dataLayerName: 'EventDataLayer'
    }
    TagManager.dataLayer(tagManagerArgs);
  };

  useEffect(() => {
    const tagManagerArgs = {
      gtmId: 'GTM-NJCT8JRC',
      dataLayer: {
        userProject: 'edco24',
        page: window.location.pathname
      },
      dataLayerName: 'PageDataLayer'
    }
    TagManager.initialize(tagManagerArgs);
    TagManager.dataLayer(tagManagerArgs);
  }, []);

  const getSchedule = useCallback(() => {
    fetch(`/api/schedule/${userGUID || ''}` + (selectedGroupGUID ? `/${selectedGroupGUID}` : ''))
      .then((response) => {
        if (response.ok) { // Checks if the status is in the range 200-299
          return response.json();
        } else {
          return null;
        }
      })
      .then((results) => {
        if (results) {
          // setData(html.html); // Only set data if HTTP status is 200-299
          const rankings = [...results.json]
            ?.map(item => item.stages)
            ?.flat()
            ?.map(item => item.sets)
            ?.flat()
            ?.filter(item => item.status)
            ?.map(item => { return { id: item.id, rank: item.status, sync: true } });

          if (rankings.length > 0) {
            setUserSelections({ user: null, selections: rankings })
            localStorage.setItem(`savedSelections-${userGUID}`, JSON.stringify({ user: null, selections: rankings }));
          } else if (!localStorage.getItem(`savedSelections-${userGUID}`) && localStorage.getItem(`savedSelections-null`)) {
            const nullSelections = JSON.parse(localStorage.getItem('savedSelections-null'))?.selections;
            if (nullSelections.length > 0) {
              importNullSchedule(nullSelections);
            }
          }
          setJson(results.json); // Only set data if HTTP status is 200-299
        }
      })
      .catch((error) => {
      });
  }, [userGUID, selectedGroupGUID, setUserSelections, setJson])

  const importNullSchedule = (selections) => {
    fetch(`/api/user-schedule/`, {
      method: 'POST',
      body: JSON.stringify({
        userScheduleUserGUID: userGUID,
        userScheduleImportedSchedule: selections,
      }),
      headers: { 'Content-Type': 'application/json' },
    })
      .then((response) => {
        if (response.ok) { // Checks if the status is in the range 200-299
          getSchedule();
          localStorage.removeItem('savedSelections-null');
        } else {
          return null;
        }
      })
      .catch((error) => {
      });
  };

  useEffect(() => {
    localStorage.setItem(`json-${userGUID}`, JSON.stringify(json));
  }, [json, userGUID]);

  useEffect(() => {
    localStorage.setItem('selectedGroupGUID', selectedGroupGUID);
  }, [selectedGroupGUID]);

  useEffect(() => {
    localStorage.setItem(`scheduleSelection`, scheduleSelection);
  }, [scheduleSelection]);

  useEffect(() => {
    getSchedule();
  }, [getSchedule]);

  useEffect(() => {
    let controller = new AbortController();
    const FIVE_MIN = 10 * 60 * 1000;

    const shouldRun = () => navigator.onLine && document.visibilityState === 'visible';

    const tick = () => {
      controller.abort();                // cancel any in-flight request
      controller = new AbortController();
      if (shouldRun()) getSchedule(controller.signal);
    };

    // run once immediately if allowed
    tick();

    const id = setInterval(tick, FIVE_MIN);

    const onVisibility = () => {
      if (shouldRun()) tick();
      else controller.abort();
    };
    const onOnline = () => tick();
    const onOffline = () => controller.abort();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      clearInterval(id);
      controller.abort();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [getSchedule]);

  const screenObj = {
    optionsBarHeight,
    windowWidth,
    windowHeight,
    devicePixelRatio,
    deviceWidth,
    deviceHeight,
    topPadding,
    bottomPadding,
    statusBarPadding,
    tableSideMargins,
    horizontal,
  }

  const friendSchedules4 = friendSchedules.filter(schedule => schedule.schedule === 4).map(schedule => schedule.name).sort((a, b) => a.localeCompare(b)) || [];
  const friendSchedules3 = friendSchedules.filter(schedule => schedule.schedule === 3).map(schedule => schedule.name).sort((a, b) => a.localeCompare(b)) || [];

  return (
    <>
      {showInfoModal && <div
        onClick={() => setShowInfoModal(false)}
        className="modal-info">
        <h1>{infoModalArtist}</h1>
        {friendSchedules4.length > 0 &&
          <div className='friend-schedules-4-container'>
            <h1><FontAwesomeIcon icon={faThumbsUp} /></h1>
            {friendSchedules4.map(friend => (
              <h2 key={friend}>{friend}</h2>
            ))}
          </div>
        }
        {friendSchedules3.length > 0 &&
          <div className="friend-schedules-3-container">
            <h1><FontAwesomeIcon icon={faQuestion} /></h1>
            {friendSchedules3.map(friend => (
              <h2 key={friend}>{friend}</h2>
            ))}
          </div>
        }
      </div>}
      {json?.length > 0 && <>
        {page === 'help' &&
          <HelpView
            screenObj={screenObj}
            userSelections={userSelections}
            setUserSelections={setUserSelections}
            setPage={setPage}
            canReset={userSelections?.selections?.length > 0 || false}
          />
        }
        {page === 'options' &&
          <OptionsView
            screenObj={screenObj}
            logButtonClick={logButtonClick}
            userGUID={userGUID}
            setUserGUID={setUserGUID}
            setPage={setPage}
            json={json}
            setJson={setJson}
            userSelections={userSelections}
            setUserSelections={setUserSelections}
            scheduleSelection={scheduleSelection}
          />}
        {page === 'user' &&
          <UserView
            userGUID={userGUID}
            setUserGUID={setUserGUID}
            setPage={setPage}
            json={json}
            setJson={setJson}
            userSelections={userSelections}
            setUserSelections={setUserSelections}
            scheduleSelection={scheduleSelection}
            setSelectedGroupGUID={setSelectedGroupGUID}
            getSchedule={getSchedule}
            online={online}
          />}
        {page === 'calendar' &&
          <CalendarView
            showInfoModal={showInfoModal}
            screenObj={screenObj}
            getSchedule={getSchedule}
            json={json}
            setPage={setPage}
            scheduleSelection={scheduleSelection}
            setScheduleSelection={setScheduleSelection}
            userGUID={userGUID}
            setUserGUID={setUserGUID}
            userSelections={userSelections}
            handleChangeUserSelections={handleChangeUserSelections}
            online={online}
            selectedGroupGUID={selectedGroupGUID}
          />
        }
      </>
      }
    </>
  );
}

export default Schedule;
